import {DateTime} from 'luxon';

import Settings from 'Services/Settings';

/**
 * @method resourceDateIsBeforeNow
 * @param {array} resourceDate
 * @return {boolean}
 */
const resourceDateIsBeforeNow = (resourceDate) => {
    const now = DateTime.now();

    const resourceDateObject = DateTime.fromISO(resourceDate.iso_string, {zone: 'UTC'});

    return (resourceDateObject < now);
}

/**
 * @method resourceDateIsAfterNow
 * @param {array} resourceDate
 * @return {boolean}
 */
const resourceDateIsAfterNow = (resourceDate) => {
    const now = DateTime.now();

    const resourceDateObject = DateTime.fromISO(resourceDate.iso_string, {zone: 'UTC'});

    return (resourceDateObject > now);
}

/**
 * @method resourceDateIsBefore
 *
 * Date must be supplied as UTC.
 *
 * @param {array} resourceDate
 * @param {int} day
 * @param {int} month
 * @param {int} year
 * @param {int} hour
 * @param {int} min
 * @param {int} second
 * @return {boolean}
 */
const resourceDateIsBefore = (resourceDate, day, month, year, hour, min, second) => {
    const staticDateObject = dateTimeFromBreakdown(day, month, year, hour, min, second);

    const resourceDateObject = DateTime.fromISO(resourceDate.iso_string, {zone: 'UTC'});

    return (resourceDateObject < staticDateObject);
}

/**
 * @method resourceDateIsAfter
 *
 * Date must be supplied as UTC.
 *
 * @param {array} resourceDate
 * @param {int} day
 * @param {int} month
 * @param {int} year
 * @param {int} hour
 * @param {int} min
 * @param {int} second
 * @return {boolean}
 */
const resourceDateIsAfter = (resourceDate, day, month, year, hour, min, second) => {
    const staticDateObject = dateTimeFromBreakdown(day, month, year, hour, min, second);

    const resourceDateObject = DateTime.fromISO(resourceDate.iso_string, {zone: 'UTC'});

    return (resourceDateObject > staticDateObject);
}

/**
 * @method resourceDateIsToday
 * @param {array} resourceDate
 * @return {boolean}
 */
const resourceDateIsToday = (resourceDate) => {
    const resourceDateObject = DateTime.fromISO(resourceDate.iso_string, {zone: resourceDate.timezone});
    const now = DateTime.now();

    return resourceDateObject.hasSame(now, "day");
}

/**
 * @method isBeforeNow
 *
 * Date must be supplied as UTC.
 *
 * @param {int} day
 * @param {int} month
 * @param {int} year
 * @param {int} hour
 * @param {int} min
 * @param {int} second
 * @return {boolean}
 */
const isBeforeNow = (day, month, year, hour, min, second) => {
    const now = DateTime.now();

    const staticDateObject = dateTimeFromBreakdown(day, month, year, hour, min, second);

    return (staticDateObject < now);
}

/**
 * @method isAfterNow
 *
 * Date must be supplied as UTC.
 *
 * @param {int} day
 * @param {int} month
 * @param {int} year
 * @param {int} hour
 * @param {int} min
 * @param {int} second
 * @return {boolean}
 */
const isAfterNow = (day, month, year, hour, min, second) => {
    const now = DateTime.now();

    const staticDateObject = dateTimeFromBreakdown(day, month, year, hour, min, second);

    return (staticDateObject > now);
}

/**
 * @method dateTimeFromBreakdown
 *
 * Date must be supplied as UTC.
 *
 * @param {int} day
 * @param {int} month
 * @param {int} year
 * @param {int} hour
 * @param {int} min
 * @param {int} second
 * @return {DateTime}
 */
const dateTimeFromBreakdown = (day, month, year, hour, min, second) => {
    return DateTime.utc(year, month, day, hour, min, second);
}

/**
 * @method dateToString
 *
 * Date must be supplied as dd/mm/yyyy.
 *
 * Reference: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
 *
 * @param {string} date
 * @return {string}
 */
const dateToString = (date) => {
    let object = DateTime.fromFormat(date, "dd/LL/yyyy");

    let date_string = '';

    // If language is set to English then return a fully formatted string
    // else return a language compatible string.

    if (Settings.data.language === 'en') {
        // Monday 8th February.
        date_string = object.toFormat('EEEE d')
            + getDayOfMonthSuffix(object.toFormat('d'))
            + ' ' + object.toFormat('MMMM');
    } else {
        // Monday February 8.
        date_string = object.toFormat('EEEE') + ' ' + object.toLocaleString({ month: 'long', day: 'numeric' });
    }

    return date_string;
}

/**
 * @method timeToTwelveHour
 *
 * Convert a 24 hour time string (HH:mm or HH:mm:ss) into a 12 hour
 * string, e.g. "14:30" becomes "2:30pm". Used so times read the same
 * way for a global audience.
 *
 * @param {string} time
 * @return {string}
 */
const timeToTwelveHour = (time) => {
    if (!time) return time;

    const object = DateTime.fromFormat(time, 'HH:mm:ss');

    const parsed = object.isValid ? object : DateTime.fromFormat(time, 'HH:mm');

    if (!parsed.isValid) return time;

    return parsed.toFormat('h:mma').toLowerCase();
}

/**
 * @method dateTimeToTwelveHour
 *
 * Convert a "dd/mm/yyyy @ HH:mm" string into a 12 hour string,
 * e.g. "07/10/2026 @ 14:30" becomes "07/10/2026 @ 2:30pm".
 *
 * @param {string} date_time
 * @return {string}
 */
const dateTimeToTwelveHour = (date_time) => {
    if (!date_time) return date_time;

    return date_time.replace(/\b([01]?\d|2[0-3]):([0-5]\d)\b/, (match) => timeToTwelveHour(match));
}

/**
 * @method getDayOfMonthSuffix
 *
 * @param {string} num
 * @return {string}
 */
const getDayOfMonthSuffix = (num) => {
    const th = 'th'
    const rd = 'rd'
    const nd = 'nd'
    const st = 'st'

    if (num == 11 || num == 12 || num == 13) return th

    let lastDigit = num.toString().slice(-1)

    switch (lastDigit) {
        case '1': return st
        case '2': return nd
        case '3': return rd
        default:  return th
    }
}

/**
 * @method getTargetDateObject
 *
 * Read the MIX_TARGET_DATE environment variable (exposed by Laravel Mix) and
 * return a Luxon DateTime in UTC, or null if not available/invalid.
 */
const getTargetDateObject = () => {
    const iso = process.env.MIX_TARGET_DATE;
    if (!iso) return null;

    try {
        return DateTime.fromISO(iso, { zone: 'UTC' });
    } catch (e) {
        return null;
    }
}

/**
 * @method getTargetDateString
 *
 * @param {boolean} withWeekday - include weekday in output (e.g. "Wednesday 7 October 2026")
 * @return {string}
 */
const getTargetDateString = (withWeekday = false) => {
    const dt = getTargetDateObject();
    if (!dt) {
        // fallback to the original hard-coded strings used previously
        return withWeekday ? 'Wednesday 7 October 2026' : '7 October 2026';
    }

    if (withWeekday) {
        return dt.toFormat('EEEE d LLLL yyyy');
    }

    return dt.toFormat('d LLLL yyyy');
}

export {
    resourceDateIsBeforeNow,
    resourceDateIsAfterNow,
    resourceDateIsBefore,
    resourceDateIsAfter,
    resourceDateIsToday,
    isBeforeNow,
    isAfterNow,
    dateToString,
    timeToTwelveHour,
    dateTimeToTwelveHour,
    getDayOfMonthSuffix,
    /**
     * Return the application's target date (from MIX_TARGET_DATE) as a Luxon DateTime
     * or null if not available.
     */
    getTargetDateObject,
    /**
     * Return the application's target date as a formatted string.
     * If withWeekday is true the format will be: "Wednesday 7 October 2026"
     * otherwise: "7 October 2026".
     * Falls back to the original hard-coded strings when MIX_TARGET_DATE is not set.
     */
    getTargetDateString
}
